// Capture JPEG still from local video File (upload / Matrix thumbnail_url).
export async function createVideoPosterFile(file: File): Promise<File | null> {
  if (!file.type.startsWith('video/') || typeof document === 'undefined') {
    return null
  }

  return new Promise(resolve => {
    const video = document.createElement('video')
    const objectUrl = URL.createObjectURL(file)
    let settled = false

    const finish = (result: File | null) => {
      if (settled) {
        return
      }
      settled = true
      URL.revokeObjectURL(objectUrl)
      resolve(result)
    }

    const captureFrame = () => {
      try {
        const width = video.videoWidth
        const height = video.videoHeight
        if (!width || !height) {
          finish(null)
          return
        }

        const maxEdge = 640
        const scale = Math.min(1, maxEdge / Math.max(width, height))
        const canvas = document.createElement('canvas')
        canvas.width = Math.max(1, Math.round(width * scale))
        canvas.height = Math.max(1, Math.round(height * scale))
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          finish(null)
          return
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(
          blob => {
            if (!blob) {
              finish(null)
              return
            }
            const baseName = file.name.replace(/\.[^.]+$/, '') || 'video'
            finish(new File([blob], `${baseName}-poster.jpg`, { type: 'image/jpeg' }))
          },
          'image/jpeg',
          0.82,
        )
      } catch {
        finish(null)
      }
    }

    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    video.onerror = () => finish(null)
    video.onseeked = () => captureFrame()

    video.onloadeddata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0
      const seekTo = duration > 0 ? Math.min(0.25, duration * 0.05) : 0
      if (seekTo <= 0 || Math.abs(video.currentTime - seekTo) < 0.001) {
        captureFrame()
        return
      }
      try {
        video.currentTime = seekTo
      } catch {
        captureFrame()
      }
    }

    // Safety: some browsers never fire loadeddata for odd codecs.
    window.setTimeout(() => {
      if (!settled && video.videoWidth > 0) {
        captureFrame()
      } else if (!settled) {
        finish(null)
      }
    }, 4000)

    video.src = objectUrl
  })
}
